import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Bootstrap, Spend } from "../../shared/types";
import { api } from "../lib/api";
import { formatMoney, formatDayMonth, todayISO, daysBetween, shiftPeriod } from "../lib/format";
import Modal from "./Modal";

// Shift an ISO date by whole days.
function addDaysISO(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
const weekday = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
};

interface WeekBucket {
  index: number;
  start: string;
  end: string;
  days: number;
  targetMinor: number;
  actualMinor: number;
  elapsedDays: number;       // days of this week gone by (0 future, full if past)
  expectedMinor: number;     // what an even pace would have you spend by today
  daysLeftInWeek: number;    // days remaining in the week after today
  isCurrent: boolean;
  isPast: boolean;
  isFuture: boolean;
  byDay: { date: string; amountMinor: number }[];
  topDay: { date: string; amountMinor: number } | null;
  topCategory: { name: string; amountMinor: number } | null;
}

export default function SafeToSpendModal({
  data,
  period,
  onClose,
}: {
  data: Bootstrap;
  period: string;
  onClose: () => void;
}) {
  const [spends, setSpends] = useState<Spend[] | null>(null);
  const [prevSpentMinor, setPrevSpentMinor] = useState<number | null>(null);

  useEffect(() => {
    api.listSpends(period).then((r) => setSpends(r.spends)).catch(() => setSpends([]));
    api
      .listSpends(shiftPeriod(period, -1))
      .then((r) => setPrevSpentMinor(r.spends.filter((s) => s.kind === "out").reduce((t, s) => t + s.amountMinor, 0)))
      .catch(() => setPrevSpentMinor(null));
  }, [period]);

  const { funds, summary } = data;
  const safe = funds.safeToSpendMinor;
  const spentThisCycle = funds.spentThisMonthMinor;
  const cycleStart = summary.cycleStart;
  const cycleEnd = summary.cycleEnd;
  const today = todayISO();
  const cycleDays = daysBetween(cycleStart, cycleEnd) + 1;
  // Discretionary pot for the whole cycle = what's still safe to spend + what
  // you've already spent. Spread over the cycle's days = a daily rate.
  const pot = (safe ?? 0) + spentThisCycle;
  const dailyRate = cycleDays > 0 ? pot / cycleDays : 0;

  const weeks = useMemo<WeekBucket[]>(() => {
    if (spends == null) return [];
    const out = spends.filter((s) => s.kind === "out");
    const buckets: WeekBucket[] = [];
    let wkStart = cycleStart;
    let i = 1;
    while (wkStart <= cycleEnd) {
      const rawEnd = addDaysISO(wkStart, 6);
      const wkEnd = rawEnd > cycleEnd ? cycleEnd : rawEnd;
      const days = daysBetween(wkStart, wkEnd) + 1;
      const inWeek = out.filter((s) => s.occurredOn >= wkStart && s.occurredOn <= wkEnd);

      const byDayMap = new Map<string, number>();
      const byCatMap = new Map<string, number>();
      for (const s of inWeek) {
        byDayMap.set(s.occurredOn, (byDayMap.get(s.occurredOn) ?? 0) + s.amountMinor);
        const cat = s.categoryName ?? "Uncategorised";
        byCatMap.set(cat, (byCatMap.get(cat) ?? 0) + s.amountMinor);
      }
      const byDay = [...byDayMap.entries()].map(([date, amountMinor]) => ({ date, amountMinor })).sort((a, b) => a.date.localeCompare(b.date));
      const actualMinor = inWeek.reduce((t, s) => t + s.amountMinor, 0);
      const topDay = byDay.length ? [...byDay].sort((a, b) => b.amountMinor - a.amountMinor)[0] : null;
      const topCatEntry = [...byCatMap.entries()].sort((a, b) => b[1] - a[1])[0];

      // How far INTO this week we are, so a partial week is judged against the
      // spend you'd expect BY TODAY (matches the Pace tile), not the full week.
      const elapsedDays = today < wkStart ? 0 : today > wkEnd ? days : daysBetween(wkStart, today) + 1;
      const expectedMinor = Math.round(dailyRate * elapsedDays);
      const daysLeftInWeek = Math.max(0, days - elapsedDays);

      buckets.push({
        index: i,
        start: wkStart,
        end: wkEnd,
        elapsedDays,
        expectedMinor,
        daysLeftInWeek,
        days,
        targetMinor: Math.round(dailyRate * days),
        actualMinor,
        isCurrent: today >= wkStart && today <= wkEnd,
        isPast: wkEnd < today,
        isFuture: wkStart > today,
        byDay,
        topDay,
        topCategory: topCatEntry ? { name: topCatEntry[0], amountMinor: topCatEntry[1] } : null,
      });
      wkStart = addDaysISO(wkStart, 7);
      i++;
    }
    return buckets;
  }, [spends, cycleStart, cycleEnd, dailyRate, today]);

  // --- Pace (whole cycle) ---
  const daysElapsed = today < cycleStart ? 0 : today > cycleEnd ? cycleDays : daysBetween(cycleStart, today) + 1;
  const expectedByToday = Math.round(dailyRate * daysElapsed);
  const paceDiff = expectedByToday - spentThisCycle; // + = under pace (good)

  // --- Run-out projection ---
  const avgDaily = daysElapsed > 0 ? spentThisCycle / daysElapsed : 0;
  let projection: { lasts: boolean; text: string } | null = null;
  if (safe != null) {
    if (avgDaily <= 0) {
      projection = { lasts: true, text: "No day-to-day spending yet this cycle." };
    } else {
      const daysAtRate = Math.floor(safe / avgDaily);
      const runOut = addDaysISO(today, Math.max(0, daysAtRate));
      if (safe <= 0) {
        projection = { lasts: false, text: "You've used all your safe-to-spend for this cycle." };
      } else if (runOut >= cycleEnd) {
        projection = { lasts: true, text: "At your current rate, this lasts to payday. 👍" };
      } else {
        const early = daysBetween(runOut, cycleEnd);
        projection = { lasts: false, text: `At your current rate, £0 around ${formatDayMonth(runOut)} — ${early} day${early === 1 ? "" : "s"} before payday.` };
      }
    }
  }

  const daysToPayday = today > cycleEnd ? 0 : daysBetween(today, cycleEnd) + 1;
  const prevPct = prevSpentMinor && prevSpentMinor > 0
    ? Math.round(((spentThisCycle - prevSpentMinor) / prevSpentMinor) * 100)
    : null;

  return (
    <Modal title="Safe to spend" onClose={onClose}>
      <div className="stw">
        <div className="stw-hero">
          <span className={"stw-hero__value" + (safe != null && safe < 0 ? " stw-hero__value--neg" : "")}>
            {safe != null ? formatMoney(safe) : "—"}
          </span>
          <span className="stw-hero__sub">
            Money left after every bill, with {daysToPayday} day{daysToPayday === 1 ? "" : "s"} to payday.
          </span>
        </div>

        <p className="stw-explain">
          Your spending pot this cycle is <strong>{formatMoney(pot)}</strong> ({formatMoney(spentThisCycle)} spent
          + {formatMoney(safe ?? 0)} safe) — about <strong>{formatMoney(Math.round(dailyRate))}/day</strong> across
          the {cycleDays}-day cycle. Weeks run from payday: {formatDayMonth(cycleStart)}–{formatDayMonth(cycleEnd)}.
        </p>

        {/* Insight tiles */}
        <div className="stw-insights">
          <div className={"stw-insight " + (paceDiff >= 0 ? "stw-insight--good" : "stw-insight--bad")}>
            <span className="stw-insight__label">Pace</span>
            <strong>{paceDiff >= 0 ? `${formatMoney(paceDiff)} under` : `${formatMoney(-paceDiff)} over`}</strong>
            <span className="stw-insight__note">{paceDiff >= 0 ? "ahead of budget for today" : "spending faster than budget"}</span>
          </div>
          {projection && (
            <div className={"stw-insight " + (projection.lasts ? "stw-insight--good" : "stw-insight--bad")}>
              <span className="stw-insight__label">Projection</span>
              <span className="stw-insight__note stw-insight__note--lg">{projection.text}</span>
            </div>
          )}
          {prevSpentMinor != null && (
            <div className="stw-insight">
              <span className="stw-insight__label">Vs last cycle</span>
              <strong>{formatMoney(spentThisCycle)}</strong>
              <span className="stw-insight__note">
                last cycle {formatMoney(prevSpentMinor)}
                {prevPct != null && <> · {prevPct >= 0 ? "↑" : "↓"} {Math.abs(prevPct)}%</>}
              </span>
            </div>
          )}
        </div>

        <h3 className="stw-h">By week</h3>
        {spends == null ? (
          <div className="state">Loading…</div>
        ) : (
          <div className="stw-weeks">
            {weeks.map((w) => <WeekRow key={w.index} w={w} />)}
          </div>
        )}
      </div>
    </Modal>
  );
}

function WeekRow({ w }: { w: WeekBucket }) {
  const [open, setOpen] = useState(w.isCurrent);
  const pct = w.targetMinor > 0 ? Math.round((w.actualMinor / w.targetMinor) * 100) : 0;
  const remaining = w.targetMinor - w.actualMinor;
  const over = w.actualMinor > w.targetMinor;              // over the WHOLE week's budget
  // In-progress week: are you ahead of where an even pace would put you TODAY?
  const inProgress = w.isCurrent && w.elapsedDays > 0 && w.elapsedDays < w.days;
  const aheadOfPace = inProgress && w.actualMinor > w.expectedMinor && !over;
  const markPct = inProgress ? Math.round((w.elapsedDays / w.days) * 100) : null;
  const barColour = over ? "var(--color-status-red)" : aheadOfPace ? "var(--color-status-amber)" : "var(--color-primary)";

  return (
    <div className={"stw-week" + (w.isCurrent ? " stw-week--current" : "") + (w.isFuture ? " stw-week--future" : "")}>
      <button className="stw-week__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <div className="stw-week__title">
          <span className="stw-week__name">
            Week {w.index}
            {w.isCurrent && <span className="stw-week__now">This week</span>}
          </span>
          <span className="stw-week__dates">{formatDayMonth(w.start)} – {formatDayMonth(w.end)}</span>
        </div>
        <div className="stw-week__figs">
          <span className={"stw-week__amt" + (over || aheadOfPace ? " stw-week__amt--over" : "")}>{formatMoney(w.actualMinor)}</span>
          <span className="stw-week__of">of {formatMoney(w.targetMinor)}</span>
        </div>
        <svg className={"stw-week__chev" + (open ? " is-open" : "")} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      <div className="stw-meter">
        <span style={{ width: `${Math.min(100, pct)}%`, background: barColour }} />
        {markPct != null && <i className="stw-meter__mark" style={{ left: `${markPct}%` }} title="Even-pace mark for today" />}
      </div>
      <div className="stw-week__foot">
        <span>{pct}% used</span>
        <span className={over ? "stw-over" : "stw-left"}>
          {over
            ? `Over by ${formatMoney(-remaining)}`
            : inProgress
              ? `${formatMoney(remaining)} left for ${w.daysLeftInWeek} day${w.daysLeftInWeek === 1 ? "" : "s"}`
              : `${formatMoney(remaining)} left`}
        </span>
      </div>
      {aheadOfPace && (
        <p className="stw-pace-note">
          ⚡ Ahead of pace — {formatMoney(w.actualMinor - w.expectedMinor)} more than the {formatMoney(w.expectedMinor)} you'd
          expect by day {w.elapsedDays} of {w.days}. At this rate you'll pass the week's {formatMoney(w.targetMinor)}.
        </p>
      )}

      {open && (
        <div className="stw-week__detail">
          {w.byDay.length === 0 ? (
            <p className="stw-empty">{w.isFuture ? "Not started yet." : "No spending this week."}</p>
          ) : (
            <>
              <ul className="stw-days">
                {w.byDay.map((d) => (
                  <li key={d.date} className="stw-day">
                    <span className="stw-day__name">{weekday(d.date)} {formatDayMonth(d.date)}</span>
                    <span className="stw-day__amt">{formatMoney(d.amountMinor)}</span>
                  </li>
                ))}
              </ul>
              <div className="stw-highlights">
                {w.topDay && <span>Biggest day: <strong>{weekday(w.topDay.date)} {formatDayMonth(w.topDay.date)}</strong> ({formatMoney(w.topDay.amountMinor)})</span>}
                {w.topCategory && <span>Top category: <strong>{w.topCategory.name}</strong> ({formatMoney(w.topCategory.amountMinor)})</span>}
              </div>
            </>
          )}
          <Link className="stw-link" to={`/activity?from=${w.start}&to=${w.end}`}>
            See this week in the activity log
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </Link>
        </div>
      )}
    </div>
  );
}
